export const createFullDatetime = (date, time) => {
  const [year, month, day] = date.split("-").map(Number);
  const [hours, minutes] = time.split(":").map(Number);
  const datetime = new Date(
    Date.UTC(year, month - 1, day, hours, minutes, 0, 0)
  );
  return datetime.toISOString();
};

export const formatDatetime = (isoString) => {
  if (!isoString) return "-";
  try {
    const date = new Date(isoString);
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = date.getUTCFullYear();
    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch (error) {
    return isoString;
  }
};

export const createFullDatetimeWithTimezone = (date, time) => {
  const [year, month, day] = date.split("-").map(Number);
  const [hours, minutes] = time.split(":").map(Number);

  const egyptDate = new Date(year, month - 1, day, hours, minutes, 0, 0);

  return egyptDate.toISOString();
};

export const isDateOlderThanSevenDays = (dateStr) => {
  const recordDate = new Date(dateStr);
  const today = new Date();
  const diffTime = Math.abs(today - recordDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 7;
};

export const isSameDayAsToday = (dateStr) => {
  const recordDate = new Date(dateStr);
  const today = new Date();
  recordDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return recordDate.getTime() === today.getTime();
};
// ── Future-date rule ────────────────────────────────────────────────────────
// Attendance may never be viewed or acted on for a day AFTER today. The
// backend enforces this on every attendance request (400 +
// code "FUTURE_DATE_NOT_ALLOWED"); these helpers keep the UI from offering
// actions the server is going to refuse.

/** Today as "YYYY-MM-DD" in the user's local (Egypt) calendar. */
export const getTodayDateStr = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/** true when the given date/ISO string falls on a day after today. */
export const isFutureDate = (dateStr) => {
  if (!dateStr) return false;
  const day =
    typeof dateStr === "string"
      ? dateStr.slice(0, 10)
      : new Date(dateStr).toISOString().slice(0, 10);
  return day > getTodayDateStr();
};

// ── TESTING ESCAPE HATCH ────────────────────────────────────────────────────
// ⚠️ Set back to `false` before shipping.
//
// While true, the UI stops enforcing the future-date rule: the date picker
// pages past today, future days are selectable, and row actions stay enabled
// on future-dated records — so the BACKEND guard can actually be exercised
// from the app.
//
// The backend is NOT affected by this flag. It still refuses every attendance
// request for a day after today with HTTP 400 and
// code "FUTURE_DATE_NOT_ALLOWED", which is exactly what this flag lets you
// see happening.
//
// Reads from REACT_APP_ALLOW_FUTURE_ATTENDANCE_DATES when defined, so it can
// be flipped per-environment (.env.development) instead of by editing code.
// Anything other than the string "false" leaves it on while this default is
// `true`.
const FUTURE_DATE_ENV =
  typeof process !== "undefined" && process.env
    ? process.env.REACT_APP_ALLOW_FUTURE_ATTENDANCE_DATES
    : undefined;

export const ALLOW_FUTURE_DATES =
  FUTURE_DATE_ENV === undefined ? false : FUTURE_DATE_ENV !== "false";

if (ALLOW_FUTURE_DATES && typeof console !== "undefined") {
  console.warn(
    "[attendance] ⚠️ TESTING MODE: future-date selection is unlocked in the UI " +
    "(ALLOW_FUTURE_DATES in pages/(Admin)Attendance/utils/dateUtils.js). " +
    "The backend still rejects future dates with 400 FUTURE_DATE_NOT_ALLOWED."
  );
}

/**
 * The check the UI should actually gate on: true only when the date is in the
 * future AND the testing escape hatch is off. Use this — not isFutureDate —
 * anywhere the UI disables or blocks something.
 */
export const isFutureDateBlocked = (dateStr) =>
  !ALLOW_FUTURE_DATES && isFutureDate(dateStr);

/** Upper bound for attendance date pickers — undefined while testing. */
export const getAttendanceMaxDate = () =>
  ALLOW_FUTURE_DATES ? undefined : getTodayDateStr();
